import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import CreditsWidget from './shared/CreditsWidget';
import OptimizationTypeSelector from './shared/OptimizationTypeSelector';
import CompareModal from './shared/CompareModal';
import { StatusPill } from './shared/StatusPill';
import { formatSize, formatRelativeDate, formatShortDate } from '../utils/imageUtils';
import { useThemeCrush, isImageSelectable } from '../hooks/useThemeCrush';
import './ThemeCrushPage.css';

const THEME_RECENTS_PAGE_SIZE = 10;

// Column template — checkbox | thumb | file+info | original | compressed | saving | status | actions
const ROW_COLS = '28px 44px minmax(140px,1fr) 70px 80px 64px 110px 110px';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toTitleCase(str) {
  if (!str) return '';
  return str.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function buildSectionLabel(img) {
  if (!img.sectionType) return null;
  const parts = [toTitleCase(img.sectionType)];
  if (img.blockType)  parts.push(toTitleCase(img.blockType));
  if (img.settingKey) parts.push(toTitleCase(img.settingKey));
  return parts.join(' › ');
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, tone, isLoading }) {
  return (
    <s-box background="base" border="base" borderRadius="base" padding="base">
      <s-stack gap="small">
        <s-text tone="subdued" size="small">{label}</s-text>
        {isLoading
          ? <div className="shimmer-loader shimmer-text-medium" />
          : <s-text type="strong" tone={tone}>{value}</s-text>
        }
      </s-stack>
    </s-box>
  );
}

// ─── ScanStatsBar ─────────────────────────────────────────────────────────────

function ScanStatsBar({ stats, scanMeta }) {
  const alreadyOptimized = scanMeta?.skippedCount ?? stats.alreadyOptimized;
  const totalFound = stats.total + alreadyOptimized;
  return (
    <s-grid gridTemplateColumns="1fr 1fr 1fr 1fr 1fr" gap="base">
      <StatCard label="Images Found"      value={totalFound} />
      <StatCard label="Already Optimized" value={alreadyOptimized} tone={alreadyOptimized > 0 ? 'success' : undefined} />
      <StatCard label="Optimized Now"     value={stats.optimized}  tone={stats.optimized > 0 ? 'success' : undefined} />
      <StatCard label="Not Resolvable"    value={stats.missingLibrary} tone={stats.missingLibrary > 0 ? 'critical' : undefined} />
      <StatCard label="Session Savings"   value={stats.totalSavedKb > 0 ? formatSize(stats.totalSavedKb) : '—'} tone={stats.totalSavedKb > 0 ? 'success' : undefined} />
    </s-grid>
  );
}

// ─── ThemeImageRow ────────────────────────────────────────────────────────────

function ThemeImageRow({ img, selected, imgStatus, onToggle, onCompare }) {
  const rowRef             = useRef(null);
  const rowStatus          = imgStatus?.status || (img.alreadyOptimized ? 'already_optimized' : 'waiting');
  const isProcessing       = rowStatus === 'processing';
  const isQueued           = rowStatus === 'queued';
  const isOptimized        = rowStatus === 'optimized';
  const isAlreadyOptimized = rowStatus === 'already_optimized';
  const isNotOptimizable   = img.notInFilesLibrary || !img.url || rowStatus === 'error';
  const isDisabled         = isProcessing || isQueued || isOptimized || rowStatus === 'reverted' || isAlreadyOptimized || isNotOptimizable;

  const origKb     = img.originalSize ? img.originalSize / 1024 : null;
  const optKb      = imgStatus?.result?.optimizedSize ?? null;
  const savingsPct = imgStatus?.result?.savingsPct    ?? null;
  const canCompare = isOptimized && imgStatus?.result?.newUrl;
  const hasError   = rowStatus === 'error' && imgStatus?.message;

  // Scroll this row into view when it starts processing
  useEffect(() => {
    if (isProcessing && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isProcessing]);

  return (
    <div
      ref={rowRef}
      className={[
        'batch-table-row', 'tc-image-row',
        selected              ? 'tc-row-selected'   : '',
        isProcessing          ? 'tc-row-processing' : '',
        isQueued              ? 'tc-row-queued'     : '',
        isOptimized || isAlreadyOptimized ? 'tc-row-done' : '',
      ].filter(Boolean).join(' ')}
      style={{ gridTemplateColumns: ROW_COLS, cursor: isDisabled ? 'default' : 'pointer' }}
      onClick={() => { if (!isDisabled) onToggle(); }}
    >
      {/* Checkbox */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
        <s-checkbox
          checked={selected}
          disabled={isDisabled || undefined}
          onInput={e => { e.stopPropagation(); if (!isDisabled) onToggle(); }}
        />
      </div>

      {/* Thumbnail */}
      <div className="batch-thumb-cell">
        {img.url ? (
          <img src={img.url} alt="" className="batch-file-thumbnail" onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <>
            <div className={`batch-thumb-placeholder-icon${img.notInFilesLibrary ? ' tc-thumb-missing' : ''}`}>
              <s-icon type={img.notInFilesLibrary ? 'alert-circle' : 'image'} color="subdued" interestFor={`tt-ph-${img.id}`} />
            </div>
            <s-tooltip id={`tt-ph-${img.id}`}>
              {img.missingReason || (img.notInFilesLibrary
                ? 'Ghost Reference: This image was not found in your Shopify Files or Theme Assets. It has likely been deleted.'
                : 'No preview available')}
            </s-tooltip>
          </>
        )}
      </div>

      {/* File info */}
      <div className="batch-file-name-wrap" style={{ minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500 }}
          interestFor={`tt-fn-${img.id}`}
        >
          {img.filename}
        </div>
        <s-tooltip id={`tt-fn-${img.id}`}>{img.filename}</s-tooltip>
        <span className="batch-file-sub">
          <span className={`tc-origin-badge tc-source-${img.assetSource || 'files_library'}`}>
            {img.assetSource === 'theme_asset'        ? '📁 Theme Asset' :
             img.assetSource === 'cdn_direct'         ? '🔗 CDN Direct'  :
             img.assetSource === 'theme_asset_filled' ? '📁 Asset'       :
             img.origin || img.groupType}
          </span>
          {(img.sectionType || img.jsonPath) && (() => {
            const sectionLabel = buildSectionLabel(img);
            return (
              <>
                <span className={sectionLabel ? 'tc-section-label' : 'tc-json-path'} interestFor={`tt-jp-${img.id}`}>
                  {sectionLabel || img.jsonPath.split('.').slice(-2).join('.')}
                </span>
                <s-tooltip id={`tt-jp-${img.id}`}>
                  {img.jsonPath || `${img.sectionType}${img.blockType ? ' › ' + img.blockType : ''}${img.settingKey ? ' › ' + img.settingKey : ''}`}
                </s-tooltip>
              </>
            );
          })()}
          {!img.jsonPath && img.sourcePath && img.sourcePath !== 'Direct File' && (
            <>
              <span className="tc-file-path" interestFor={`tt-sp-${img.id}`}>
                {img.sourcePath.split(',')[0].trim()}
              </span>
              <s-tooltip id={`tt-sp-${img.id}`}>{img.sourcePath.split(',')[0].trim()}</s-tooltip>
            </>
          )}
        </span>
      </div>

      {/* Original */}
      <s-text size="small" tone="subdued">{origKb != null ? formatSize(origKb) : '—'}</s-text>

      {/* Compressed */}
      <div>
        {isOptimized && optKb != null
          ? <s-text size="small" tone="success">{formatSize(optKb)}</s-text>
          : <s-text size="small" tone="subdued">—</s-text>
        }
      </div>

      {/* Saving */}
      <div>
        {isOptimized && savingsPct != null && savingsPct > 0
          ? <s-text size="small" tone="success">{savingsPct}%</s-text>
          : <s-text size="small" tone="subdued">—</s-text>
        }
      </div>

      {/* Status */}
      <div>
        <StatusPill
          status={rowStatus}
          message={imgStatus?.message}
          tooltipId={imgStatus?.message ? `tt-st-${img.id}` : undefined}
        />
      </div>

      {/* Actions */}
      <div className="batch-actions" onClick={e => e.stopPropagation()}>
        {canCompare && (
          <s-button
            variant="secondary"
            size="slim"
            onClick={() => onCompare({
              originalUrl:     img.url,
              optimizedUrl:    imgStatus.result.newUrl,
              originalName:    img.filename,
              originalSizeKb:  imgStatus.result.originalSize,
              optimizedSizeKb: imgStatus.result.optimizedSize,
              savingsPercent:  imgStatus.result.savingsPct,
            })}
          >
            Compare
          </s-button>
        )}
        {hasError && (
          <s-text tone="critical" size="small">{imgStatus.message}</s-text>
        )}
        {rowStatus === 'skipped' && imgStatus?.message && (
          <s-text tone="subdued" size="small" title={imgStatus.message}>
            {imgStatus.message.length > 35 ? imgStatus.message.slice(0, 33) + '…' : imgStatus.message}
          </s-text>
        )}
        {!canCompare && !hasError && rowStatus !== 'skipped' && <s-text size="small" tone="subdued">—</s-text>}
      </div>
    </div>
  );
}

// ─── ThemeGroup (Accordion) ───────────────────────────────────────────────────

function ThemeGroup({ group, selectedImages, imageStatuses, isExpanded, onToggleExpand, onToggleGroupAll, onToggleOne, onRevert, onCompare, onOptimizeGroup, optimizing }) {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const groupTopRef = useRef(null);

  const optimizedCount  = group.images.filter(img => imageStatuses.get(img.id)?.status === 'optimized').length;
  const processingCount = group.images.filter(img => imageStatuses.get(img.id)?.status === 'processing').length;
  const revertedCount   = group.images.filter(img => imageStatuses.get(img.id)?.status === 'reverted').length;
  const alreadyCount    = group.images.filter(img => img.alreadyOptimized && !imageStatuses.has(img.id)).length;

  const selectableImages = group.images.filter(img => isImageSelectable(img, imageStatuses));
  const selectedInGroup  = selectableImages.filter(img => selectedImages.has(img.id)).length;
  const noneSelectable   = selectableImages.length === 0;
  const allSelected      = !noneSelectable && selectedInGroup === selectableImages.length;
  const someSelected     = selectedInGroup > 0 && !allSelected;
  const totalPages       = Math.ceil(group.images.length / pageSize);

  return (
    <div ref={groupTopRef} className="batch-card batch-card-info">
      {/* Accordion header */}
      <div className="batch-card-header" onClick={() => onToggleExpand(group.key)}>
        <s-stack direction="inline" gap="small" alignItems="center" style={{ flex: 1, minWidth: 0 }}>
          <s-button
            variant="tertiary"
            icon={isExpanded ? 'chevron-down' : 'chevron-right'}
            onClick={e => { e.stopPropagation(); onToggleExpand(group.key); }}
          />
          <span className="info-tooltip">
            <s-text type="strong" size="small">{group.label}</s-text>
            <span className="info-tooltip-bubble">{group.key === 'global' ? 'config/settings_data.json' : group.key}</span>
          </span>
          <s-badge tone="info" size="small">{group.images.length} image{group.images.length !== 1 ? 's' : ''}</s-badge>
          {processingCount > 0 && <s-badge tone="warning" size="small"><span className="tc-spin">⟳</span> {processingCount} processing</s-badge>}
          {optimizedCount  > 0 && <s-badge tone="success" size="small">{optimizedCount} optimized</s-badge>}
          {alreadyCount    > 0 && <s-badge tone="success" size="small">{alreadyCount} already done</s-badge>}
          {revertedCount   > 0 && <s-badge tone="attention" size="small">{revertedCount} reverted</s-badge>}
        </s-stack>

        <div style={{ flexShrink: 0, paddingLeft: 12, display: 'flex', alignItems: 'center', gap: 12 }} onClick={e => e.stopPropagation()}>
          {selectedInGroup > 0 && (
            <s-button variant="primary" size="slim" loading={optimizing || undefined} onClick={() => onOptimizeGroup(group.images)}>
              Optimize Selected ({selectedInGroup})
            </s-button>
          )}
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-text size="small" tone={noneSelectable ? 'disabled' : 'subdued'}>Select all available</s-text>
            <s-checkbox
              checked={allSelected}
              indeterminate={someSelected || undefined}
              disabled={noneSelectable || undefined}
              onInput={() => { if (!noneSelectable) onToggleGroupAll(group.images, !allSelected); }}
            />
          </s-stack>
        </div>
      </div>

      {/* Table body — collapsed via max-height/opacity, always mounted so images load */}
      <div style={{ maxHeight: isExpanded ? 9999 : 0, overflow: 'hidden', opacity: isExpanded ? 1 : 0, transition: 'max-height 0.25s ease, opacity 0.15s ease' }}>
        {/* Column headers */}
        <div className="batch-table-header" style={{ gridTemplateColumns: ROW_COLS }}>
          <span /><span />
          <s-text size="small" type="strong">Image / Source</s-text>
          <s-text size="small" type="strong">Original</s-text>
          <s-text size="small" type="strong">Compressed</s-text>
          <s-text size="small" type="strong">Saving</s-text>
          <s-text size="small" type="strong">Status</s-text>
          <s-text size="small" type="strong">Actions</s-text>
        </div>

        {group.images.slice((page - 1) * pageSize, page * pageSize).map(img => (
          <ThemeImageRow
            key={img.id}
            img={img}
            selected={selectedImages.has(img.id)}
            imgStatus={imageStatuses.get(img.id)}
            onToggle={() => onToggleOne(img.id)}
            onRevert={onRevert}
            onCompare={onCompare}
          />
        ))}

        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--p-color-border-subdued)' }}>
            <s-text size="small" tone="subdued">
              Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, group.images.length)} of {group.images.length}
            </s-text>
            <s-stack direction="inline" gap="tight">
              <s-button
                size="small" variant="tertiary" icon="chevron-left"
                disabled={page === 1 || undefined}
                onClick={() => setPage(p => p - 1)}
              />
              <s-button
                size="small" variant="tertiary" icon="chevron-right"
                disabled={page === totalPages || undefined}
                onClick={() => setPage(p => p + 1)}
              />
            </s-stack>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ThemeRecents ─────────────────────────────────────────────────────────────

function ThemeRecentsRow({ rec, onCompare }) {
  const [urlBroken,      setUrlBroken]      = useState(false);
  const [originalBroken, setOriginalBroken] = useState(false);
  const thumbUrl = rec.optimizedUrl || rec.originalUrl;
  // Original URL is "distinct" when it differs from the thumb — meaning optimized file still
  // exists but the original source may have been deleted separately from Shopify Files.
  const originalDistinct = rec.originalUrl && rec.originalUrl !== thumbUrl;
  const isRemovedFromShopify = urlBroken || originalBroken;

  const savingsPct = rec.originalSizeKb && rec.optimizedSizeKb
    ? Math.round(((rec.originalSizeKb - rec.optimizedSizeKb) / rec.originalSizeKb) * 100)
    : null;

  return (
    <div className="tc-recents-row">
      {/* Hidden probe to detect when the original source file was deleted from Shopify Files */}
      {originalDistinct && (
        <img
          src={rec.originalUrl}
          style={{ display: 'none', position: 'absolute', width: 0, height: 0 }}
          alt=""
          onError={() => setOriginalBroken(true)}
          onLoad={() => setOriginalBroken(false)}
        />
      )}
      <div className="tc-recents-thumb">
        {thumbUrl && !urlBroken
          ? <img src={thumbUrl} alt="" className="tc-recents-thumb-img" onError={() => setUrlBroken(true)} />
          : <div className="batch-thumb-placeholder-icon"><s-icon type="image" color="subdued" /></div>
        }
      </div>
      <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500 }} title={rec.originalFilename}>
        {rec.originalFilename}
      </div>
      <div style={{ minWidth: 0 }}>
        {rec.sourceFile
          ? <span className="info-tooltip">
              <span className="tc-file-path">{rec.sourceFile.split('/').pop()}</span>
              <span className="info-tooltip-bubble">{rec.sourceFile}</span>
            </span>
          : <s-text size="small" tone="subdued">—</s-text>
        }
      </div>
      <s-text size="small" tone="subdued">{rec.originalSizeKb ? formatSize(rec.originalSizeKb) : '—'}</s-text>
      {rec.optimizedSizeKb
        ? <s-text size="small" tone="success">{formatSize(rec.optimizedSizeKb)}</s-text>
        : <s-text size="small" tone="subdued">—</s-text>
      }
      {savingsPct != null && savingsPct > 0
        ? <s-text size="small" tone="success">{savingsPct}%</s-text>
        : <s-text size="small" tone="subdued">—</s-text>
      }
      <StatusPill status={rec.status === 'optimized' ? 'optimized' : rec.status} />
      <div>
        <div style={{ fontSize: 11, fontWeight: 600 }}>{formatShortDate(rec.createdAt)}</div>
        <div style={{ fontSize: 10, color: 'var(--p-color-text-subdued)' }}>{formatRelativeDate(rec.createdAt)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {isRemovedFromShopify ? (
          <>
            <s-tooltip id={`tt-removed-${rec.id}`}>This image is no longer available in Shopify. It may have been deleted.</s-tooltip>
            <span interestFor={`tt-removed-${rec.id}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
              <s-icon type="alert-circle" color="critical" />
              <s-text tone="critical" size="small">Removed</s-text>
            </span>
          </>
        ) : rec.status === 'optimized' && rec.optimizedUrl ? (
          <s-button variant="secondary" size="slim" onClick={() => onCompare({
            originalUrl:     rec.originalUrl,
            optimizedUrl:    rec.optimizedUrl,
            originalName:    rec.originalFilename,
            originalSizeKb:  rec.originalSizeKb,
            optimizedSizeKb: rec.optimizedSizeKb,
            savingsPercent:  savingsPct,
          })}>Compare</s-button>
        ) : null}
      </div>
    </div>
  );
}

function ThemeRecents({ refreshKey, onCompare }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [page, setPage]           = useState(1);
  const [pageInfo, setPageInfo]   = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const recentsTopRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page, pageSize: THEME_RECENTS_PAGE_SIZE });
    authenticatedFetch(`/api/themes/recents?${params}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        if (data.success) { setRecords(data.records || []); setPageInfo(data.pageInfo || null); }
        else setError('Could not load recent optimizations.');
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, page, localRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(() => {
    setPage(1);
    setLocalRefresh(k => k + 1);
  }, []);

  return (
    <div ref={recentsTopRef}>
    <s-card padding="none">
      {/* Header */}
      <s-box padding="base" borderBottom="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-heading as="h3">Recent Theme Optimizations</s-heading>
            {pageInfo?.total > 0 && <s-badge tone="info">{pageInfo.total} total</s-badge>}
          </s-stack>
          <s-button variant="secondary" size="small" icon="refresh" onClick={handleRefresh}>
            Refresh
          </s-button>
        </s-stack>
      </s-box>

      {/* Loading shimmer */}
      {loading && (
        <s-stack gap="none">
          {[1, 2, 3, 4, 5].map(i => (
            <s-box key={i} padding="base" borderBottom="base">
              <div style={{ display: 'flex', width: '100%', gap: '16px', alignItems: 'center' }}>
                <div className="shimmer-loader" style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div className="shimmer-loader" style={{ width: '60%', height: 16, borderRadius: 4 }} />
                  <div className="shimmer-loader" style={{ width: '40%', height: 12, borderRadius: 4 }} />
                </div>
                <div className="shimmer-loader" style={{ width: 72, height: 22, borderRadius: 12, flexShrink: 0 }} />
              </div>
            </s-box>
          ))}
        </s-stack>
      )}

      {!loading && error && (
        <s-box padding="base"><s-banner tone="critical"><s-text>{error}</s-text></s-banner></s-box>
      )}

      {!loading && !error && records.length === 0 && (
        <s-box padding="extra-large">
          <s-stack gap="base" alignItems="center">
            <div className="es-icon">🎨</div>
            <s-stack gap="extra-tight" alignItems="center">
              <s-text type="strong" size="large" style={{ textAlign: 'center' }}>No theme optimizations yet</s-text>
              <s-text tone="subdued" style={{ textAlign: 'center', maxWidth: 380, display: 'block' }}>
                Scan a theme and optimize its images — they'll appear here for review and comparison.
              </s-text>
            </s-stack>
          </s-stack>
        </s-box>
      )}

      {!loading && !error && records.length > 0 && (
        <>
          {/* Column headers */}
          <div className="tc-recents-header-row">
            <span /><span>Filename</span><span>Source File</span>
            <span>Original</span><span>Optimized</span><span>Saving</span>
            <span>Status</span><span>Date</span>
            <span style={{ textAlign: 'center' }}>Actions</span>
          </div>

          {/* Rows */}
          {records.map(rec => (
            <ThemeRecentsRow key={rec.id} rec={rec} onCompare={onCompare} />
          ))}
        </>
      )}

      {/* Pagination */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--p-color-border-subdued)' }}>
          <s-text size="small" tone="subdued">Page {pageInfo.page} of {pageInfo.totalPages} · {pageInfo.total} records</s-text>
          <s-stack direction="inline" gap="tight">
            <s-button
              size="small" variant="tertiary" icon="chevron-left"
              disabled={!pageInfo.hasPrevious || undefined}
              onClick={() => setPage(p => p - 1)}
            />
            <s-button
              size="small" variant="tertiary" icon="chevron-right"
              disabled={!pageInfo.hasNext || undefined}
              onClick={() => setPage(p => p + 1)}
            />
          </s-stack>
        </div>
      )}
    </s-card>
    </div>
  );
}

// ─── OptimizeConfirmModal ─────────────────────────────────────────────────────
// Uses Shopify s-modal (App Bridge) with commandFor/command pattern.
// A hidden trigger button is clicked programmatically — this counts as a valid
// user-interaction origin so App Bridge always opens the overlay correctly.

function OptimizeConfirmModal({ triggerRef, isLiveTheme, imageCount, onConfirm }) {
  return (
    <>
      {/* Hidden trigger — clicked via ref from openConfirmModal() */}
      <button
        ref={triggerRef}
        commandFor="tc-confirm-modal"
        command="--show"
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />

      <s-modal id="tc-confirm-modal" heading="Confirm Image Optimization">
        <s-stack gap="base">
          {isLiveTheme && (
            <s-banner tone="critical">
              <s-stack gap="extra-tight">
                <s-text type="strong">You are optimizing your LIVE theme.</s-text>
                <s-text>
                  Changes will be applied immediately to your live store. We strongly recommend
                  testing on a duplicate theme first, verifying results look correct, and only
                  then optimizing your live theme.
                </s-text>
              </s-stack>
            </s-banner>
          )}

          <s-banner tone="warning">
            <s-stack gap="extra-tight">
              <s-text type="strong">This cannot be automatically reverted.</s-text>
              <s-text>
                Once images are optimized and written back to your theme, there is no one-click undo.
              </s-text>
            </s-stack>
          </s-banner>

          <div className="tc-confirm-info-box">
            <s-text type="strong" size="small">What you may need to do manually to restore originals:</s-text>
            <ul className="tc-confirm-list">
              <li>Open your theme editor in the Shopify admin</li>
              <li>Navigate to each affected section, block, or global setting</li>
              <li>Manually re-select or re-upload the original image in that setting</li>
            </ul>
            <s-text tone="subdued" size="small">
              Keep backups of any critical original images before proceeding.
            </s-text>
          </div>

          <s-text tone="subdued" size="small">
            You are about to optimize <strong>{imageCount} image{imageCount !== 1 ? 's' : ''}</strong>.
            Please confirm you have reviewed your selection carefully.
          </s-text>
        </s-stack>

        <s-button
          slot="secondary-actions"
          variant="secondary"
          commandFor="tc-confirm-modal"
          command="--hide"
        >
          Cancel
        </s-button>
        <s-button
          slot="primary-action"
          variant={isLiveTheme ? 'critical' : 'primary'}
          commandFor="tc-confirm-modal"
          command="--hide"
          onClick={onConfirm}
        >
          {isLiveTheme ? 'Optimize Live Theme' : 'Yes, Optimize Now'} ({imageCount})
        </s-button>
      </s-modal>
    </>
  );
}

// ─── ThemeCrushPage ───────────────────────────────────────────────────────────

const ThemeCrushPage = () => {
  const navigate = useNavigate();
  const [activeTab,  setActiveTab]  = useState(0);
  const [compareImg, setCompareImg] = useState(null);

  const {
    themes, themesLoading, selectedThemeId, scanning, scanDone, scanError, images, groups, stats, scanMeta,
    selectedImages, expandedGroups, optimizing, imageStatuses,
    optimizationType, handleOptimizationTypeChange, recentsKey,
    handleScanTheme, handleAbortScan, handleOptimizeBatch, handleOptimize,
    handleToggleOne, handleToggleGroupAll, handleToggleAllGlobal,
    handleToggleExpand, handleThemeSelect, clearSession,
  } = useThemeCrush();

  // ── Confirm-modal state ───────────────────────────────────────────────────
  const pendingOptimizeRef = useRef(null);
  const [confirmImageCount, setConfirmImageCount] = useState(0);
  const modalTriggerRef = useRef(null);

  const selectedTheme   = themes.find(t => t.id === selectedThemeId);
  const isLiveTheme     = selectedTheme?.role === 'main';
  const displayedThemes = themes.filter(t => t.role === 'main' || t.role === 'unpublished');

  const openConfirmModal = useCallback((action, count) => {
    pendingOptimizeRef.current = action;
    setConfirmImageCount(count);
    modalTriggerRef.current?.click();
  }, []);

  const handleConfirmOptimize = useCallback(() => {
    const action = pendingOptimizeRef.current;
    pendingOptimizeRef.current = null;
    if (action) action();
  }, []);

  // Refs for auto-scroll
  const resultsRef  = useRef(null);
  const scanningRef = useRef(null);

  // Scroll to scanning skeleton when scan starts
  useEffect(() => {
    if (scanning && scanningRef.current) {
      setTimeout(() => {
        scanningRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [scanning]);

  // Scroll to results when scan finishes and images have loaded
  useEffect(() => {
    if (scanDone && images.length > 0 && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [scanDone, images.length]);

  // Clear scan results when switching to the Recent Optimizations tab.
  // Calling handleThemeSelect with the current ID clears images/scanDone without changing theme.
  useEffect(() => {
    if (activeTab === 1) {
      handleThemeSelect(selectedThemeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Use a ref so the unmount cleanup always reads the latest optimizing value.
  const optimizingRef = useRef(optimizing);
  useEffect(() => { optimizingRef.current = optimizing; }, [optimizing]);

  // When navigating away from this page: if no optimization is in flight, clear
  // the persisted session so returning shows a fresh state (results are in Recents).
  // If actively optimizing, keep the session so the user can return and see progress.
  useEffect(() => {
    return () => { if (!optimizingRef.current) clearSession(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectableCount  = images.filter(img => isImageSelectable(img, imageStatuses)).length;
  const doneCount        = stats.optimized + stats.skipped + stats.errors;
  const optimizeBtnLabel = optimizing
    ? `Optimizing ${doneCount} / ${stats.selected}…`
    : `Optimize ${stats.selected} Image${stats.selected !== 1 ? 's' : ''}`;

  return (
    <div style={{ paddingBottom: '2rem' }}>
      <s-stack gap="large">

        {/* ── Page Header ── */}
        <s-stack direction="inline" justifyContent="space-between" alignItems="start">
          <s-stack gap="extra-tight">
            <s-heading as="h1">Theme Images Optimizer</s-heading>
            <s-text tone="subdued">
              Scan your active theme for images, optimize them, and update theme settings automatically.
            </s-text>
          </s-stack>
          <div className="tc-credits-top">
            <CreditsWidget onPurchase={() => navigate('/pricing')} />
          </div>
        </s-stack>

        {/* ── Tab Bar ── */}
        <div className="tab-bar-container">
          <div className="tab-container-flex">
            <button
              className={`tab-button ${activeTab === 0 ? 'tab-button-active' : ''}`}
              onClick={() => setActiveTab(0)}
            >
              Scan &amp; Optimize
            </button>
            <button
              className={`tab-button ${activeTab === 1 ? 'tab-button-active' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              Recent Optimizations
            </button>
          </div>
        </div>

        {/* ══ TAB 0: Scan & Optimize ══ */}
        {activeTab === 0 && (
          <s-stack gap="base">

            {/* Warning */}
            <s-banner tone="warning">
              <s-stack gap="extra-tight">
                <s-text>Optimizing updates images directly in your theme. <strong>Duplicate your theme before proceeding.</strong></s-text>
                <s-text><strong>Note on Re-used Images:</strong> If multiple pickers have selected the exact same image, it will only be listed here once. Once optimized, the update will apply automatically to all pickers using that image.</s-text>
              </s-stack>
            </s-banner>

            {/* ── Theme Selection Card ── */}
            <s-card padding="none">
              <s-box padding="base" borderBottom="base">
                <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                  <s-stack gap="extra-tight">
                    <s-heading as="h3">Select a Theme to Scan</s-heading>
                    <s-text tone="subdued" size="small">
                      Choose a theme below — images from templates, sections, and global settings will be discovered.
                    </s-text>
                  </s-stack>
                  <s-button
                    variant="primary"
                    loading={scanning || undefined}
                    disabled={!selectedThemeId || scanning || optimizing || themesLoading || undefined}
                    onClick={handleScanTheme}
                  >
                    {scanning ? 'Scanning…' : scanDone ? 'Re-scan Theme' : 'Scan Theme'}
                  </s-button>
                </s-stack>
              </s-box>

              {/* Optimization-in-progress lock notice */}
              {optimizing && (
                <s-box padding="base" borderBottom="base" background="warning-subdued">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <span className="tc-spin" style={{ fontSize: 16 }}>⟳</span>
                    <s-text size="small" type="strong">Optimization in progress — theme selection and scanning are locked until the current batch finishes.</s-text>
                  </s-stack>
                </s-box>
              )}

              {/* Live theme warning — right under the scan button section */}
              {!themesLoading && isLiveTheme && (
                <s-box padding="base" borderBottom="base" background="warning-subdued">
                  <div className="tc-live-theme-warning">
                    <div className="tc-live-theme-warning-icon">⚠</div>
                    <s-stack gap="extra-tight">
                      <s-text type="strong" size="small">You have selected your live theme — proceed with caution.</s-text>
                      <s-text tone="subdued" size="small">
                        Optimizing images on a live theme immediately affects your store.
                        We strongly recommend you <strong>duplicate this theme</strong> first,
                        optimize the duplicate, check everything looks correct, then make it live
                        — only optimize the live theme directly when you are fully confident.
                      </s-text>
                    </s-stack>
                  </div>
                </s-box>
              )}

              {/* Theme grid */}
              <s-box padding="base">
                {themesLoading ? (
                  <div className="tc-theme-grid">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="tc-theme-card tc-theme-card-loading">
                        <div className="shimmer-loader" style={{ width: 40, height: 40, borderRadius: 10, marginBottom: 8 }} />
                        <div className="shimmer-loader" style={{ width: '80%', height: 16, borderRadius: 4, marginBottom: 4 }} />
                        <div className="shimmer-loader" style={{ width: '60%', height: 12, borderRadius: 4 }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {displayedThemes.length === 0 ? (
                      <s-box padding="large">
                        <s-stack gap="base" alignItems="center">
                          <s-icon type="folder" color="subdued" size="large" />
                          <s-text tone="subdued">No themes available to optimize.</s-text>
                        </s-stack>
                      </s-box>
                    ) : (
                      <div className="tc-theme-grid">
                        {displayedThemes.map(theme => {
                          const isSelected = selectedThemeId === theme.id;
                          const isMain     = theme.role === 'main';
                          return (
                            <div
                              key={theme.id}
                              className={`tc-theme-card${isSelected ? ' tc-theme-card-selected' : ''}${isMain ? ' tc-theme-card-main' : ''}${optimizing ? ' tc-theme-card-locked' : ''}`}
                              onClick={() => { if (!optimizing) handleThemeSelect(theme.id); }}
                              title={optimizing ? 'Cannot change theme while optimization is in progress' : undefined}
                            >
                              <div className="tc-theme-card-icon">
                                {isMain ? <s-icon type="home" color="base" /> : <s-icon type="folder" color="base" />}
                              </div>
                              <div className="tc-theme-card-name">{theme.name}</div>
                              <div className="tc-theme-card-meta">
                                <s-badge tone={isMain ? 'success' : 'neutral'} size="small">
                                  {isMain ? 'LIVE' : 'DRAFT'}
                                </s-badge>
                              </div>
                              {isSelected && <div className="tc-theme-card-check">✓</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </s-box>

              {/* Trial/demo themes notice */}
              {!themesLoading && themes.length > displayedThemes.length && (
                <s-box padding="base" borderTop="base">
                  <s-text tone="subdued" size="small">
                    Trial and demo themes are hidden — only Live and Draft themes can be optimized.
                  </s-text>
                </s-box>
              )}
            </s-card>

            {/* ── Scanning skeleton ── */}
            {scanning && (
              <div ref={scanningRef}>
              <s-card padding="none">
                <s-box padding="base" borderBottom="base">
                  <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                    <s-stack gap="extra-tight">
                      <s-heading as="h3">Scanning theme…</s-heading>
                      <s-text tone="subdued" size="small">Discovering images across templates, sections, and global settings. This may take 1–2 minutes — please keep this tab open.</s-text>
                    </s-stack>
                    <s-button variant="secondary" tone="critical" onClick={handleAbortScan}>
                      Stop Scanning
                    </s-button>
                  </s-stack>
                </s-box>
                <s-stack gap="none">
                  {[1, 2, 3, 4, 5].map(i => (
                    <s-box key={i} padding="base" borderBottom="base">
                      <div style={{ display: 'grid', gridTemplateColumns: ROW_COLS, gap: 8, alignItems: 'center' }}>
                        <div className="shimmer-loader" style={{ width: 18, height: 18, borderRadius: 3 }} />
                        <div className="shimmer-loader" style={{ width: 40, height: 40, borderRadius: 6 }} />
                        <s-stack gap="extra-tight">
                          <div className="shimmer-loader shimmer-text-medium" />
                          <div className="shimmer-loader shimmer-text-small" />
                        </s-stack>
                        <div className="shimmer-loader" style={{ width: 48, height: 12 }} />
                        <div className="shimmer-loader" style={{ width: 48, height: 12 }} />
                        <div className="shimmer-loader" style={{ width: 36, height: 12 }} />
                        <div className="shimmer-loader" style={{ width: 80, height: 22, borderRadius: 12 }} />
                        <div className="shimmer-loader" style={{ width: 60, height: 24, borderRadius: 4 }} />
                      </div>
                    </s-box>
                  ))}
                </s-stack>
              </s-card>
              </div>
            )}

            {/* ── Scan error ── */}
            {scanError && !scanning && (
              <div className="tc-empty-state-card">
                <div className="tc-empty-state-icon" style={{ fontSize: 40 }}>⚠️</div>
                <s-heading as="h3">Scan Failed</s-heading>
                <s-text tone="critical" style={{ textAlign: 'center', maxWidth: 460 }}>
                  {scanError}
                </s-text>
                <div className="tc-empty-state-hints">
                  <div className="tc-empty-hint-item">
                    <span className="tc-empty-hint-icon">🔁</span>
                    <s-text size="small">This is usually a temporary issue — try scanning again in a few seconds.</s-text>
                  </div>
                  <div className="tc-empty-hint-item">
                    <span className="tc-empty-hint-icon">🔑</span>
                    <s-text size="small">If the error mentions permissions or access denied, the app may need to be reinstalled to pick up the latest scopes.</s-text>
                  </div>
                </div>
                <s-button variant="primary" loading={scanning || undefined} disabled={scanning || undefined} onClick={handleScanTheme}>
                  Try Again
                </s-button>
              </div>
            )}

            {/* ── No results ── */}
            {scanDone && images.length === 0 && !scanning && (
              <div className="tc-empty-state-card">
                {scanMeta.skippedCount > 0 ? (
                  <>
                    <div className="tc-empty-state-icon">✅</div>
                    <s-heading as="h3">All Theme Images Are Already Optimized</s-heading>
                    <s-text tone="subdued" style={{ textAlign: 'center', maxWidth: 460 }}>
                      We found <strong>{scanMeta.skippedCount} image{scanMeta.skippedCount !== 1 ? 's' : ''}</strong> in
                      this theme, but {scanMeta.skippedCount === 1 ? 'it has' : 'they have all'} already been optimized
                      by this app. There is nothing left to do — check the <strong>Recent Optimizations</strong> tab to
                      review your results.
                    </s-text>
                    <s-button variant="secondary" loading={scanning || undefined} disabled={scanning || undefined} onClick={handleScanTheme}>
                      Re-scan Theme
                    </s-button>
                  </>
                ) : (
                  <>
                    <div className="tc-empty-state-icon">🔍</div>
                    <s-heading as="h3">No Optimizable Images Found</s-heading>
                    <s-text tone="subdued" style={{ textAlign: 'center', maxWidth: 440 }}>
                      We scanned this theme but could not find any store images referenced in its
                      templates, sections, or global settings.
                    </s-text>
                    <div className="tc-empty-state-hints">
                      <div className="tc-empty-hint-item">
                        <span className="tc-empty-hint-icon">📁</span>
                        <s-text size="small">
                          Make sure images are uploaded to your <strong>Shopify Files library</strong> and assigned
                          in theme sections or settings (not just hardcoded in code).
                        </s-text>
                      </div>
                      <div className="tc-empty-hint-item">
                        <span className="tc-empty-hint-icon">🔄</span>
                        <s-text size="small">
                          Try selecting a different theme from the card grid above — themes vary in how they reference images.
                        </s-text>
                      </div>
                      <div className="tc-empty-hint-item">
                        <span className="tc-empty-hint-icon">🖼️</span>
                        <s-text size="small">
                          Themes using only theme asset images (uploaded via Theme Editor drag-and-drop) may not appear here.
                        </s-text>
                      </div>
                    </div>
                    <s-button variant="primary" loading={scanning || undefined} disabled={scanning || undefined} onClick={handleScanTheme}>
                      Re-scan This Theme
                    </s-button>
                  </>
                )}
              </div>
            )}

            {/* ── Scan results ── */}
            {scanDone && images.length > 0 && !scanning && (
              <div ref={resultsRef}>
              <s-stack gap="base">

                {/* Stats bar */}
                <ScanStatsBar stats={stats} scanMeta={scanMeta} />

                {/* Toolbar */}
                <s-card padding="base">
                  <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                    <s-stack direction="inline" gap="small" alignItems="center" style={{ flexWrap: 'wrap' }}>
                      <s-checkbox
                        label="Select all available"
                        checked={selectedImages.size === selectableCount && selectableCount > 0}
                        indeterminate={(selectedImages.size > 0 && selectedImages.size < selectableCount) || undefined}
                        disabled={selectableCount === 0 || undefined}
                        onInput={handleToggleAllGlobal}
                      />
                      <s-badge tone="info">{stats.total} found</s-badge>
                      {stats.alreadyOptimized > 0 && <s-badge tone="success">{stats.alreadyOptimized} already optimized</s-badge>}
                      {stats.selected   > 0 && <s-badge tone="info">{stats.selected} selected</s-badge>}
                      {stats.optimized  > 0 && <s-badge tone="success">{stats.optimized} optimized this session</s-badge>}
                      {stats.errors     > 0 && <s-badge tone="critical">{stats.errors} errors</s-badge>}
                      {stats.totalSavedKb > 0 && <s-badge tone="success">{formatSize(stats.totalSavedKb)} saved</s-badge>}
                    </s-stack>

                    <s-stack direction="inline" gap="small" alignItems="center">
                      <OptimizationTypeSelector
                        optimizationType={optimizationType}
                        onChange={handleOptimizationTypeChange}
                        disabled={optimizing}
                      />
                      <s-button
                        variant="primary"
                        loading={optimizing || undefined}
                        disabled={stats.selected === 0 || optimizing || undefined}
                        onClick={() => openConfirmModal(() => handleOptimize(), stats.selected)}
                      >
                        {optimizeBtnLabel}
                      </s-button>
                    </s-stack>
                  </s-stack>
                </s-card>

                {/* Missing references note */}
                {stats.missingLibrary > 0 && (
                  <s-stack direction="inline" gap="small" alignItems="center" style={{ padding: '6px 2px' }}>
                    <s-icon type="info" color="subdued" />
                    <s-text tone="subdued" size="small">
                      {stats.missingLibrary} image {stats.missingLibrary === 1 ? 'reference' : 'references'} in your theme could not be resolved and {stats.missingLibrary === 1 ? 'is' : 'are'} hidden — the {stats.missingLibrary === 1 ? 'file has' : 'files have'} likely been deleted from your Shopify Files library.
                    </s-text>
                  </s-stack>
                )}

                {/* Accordion groups — or fallback when all images are unresolvable */}
                {groups.length === 0 && (
                  <div className="tc-empty-state-card" style={{ padding: '32px 24px' }}>
                    <div className="tc-empty-state-icon" style={{ fontSize: 40 }}>🗂️</div>
                    <s-heading as="h3">No Actionable Images</s-heading>
                    <s-text tone="subdued" style={{ textAlign: 'center', maxWidth: 460 }}>
                      {stats.missingLibrary > 0
                        ? `All ${stats.missingLibrary} image reference${stats.missingLibrary !== 1 ? 's' : ''} found in this theme could not be resolved — the files have likely been deleted from your Shopify Files library. Only images in the Files library or theme assets can be optimized.`
                        : 'All images found in this scan are either already optimized or cannot be processed.'}
                    </s-text>
                    <s-button variant="secondary" loading={scanning || undefined} disabled={scanning || undefined} onClick={handleScanTheme}>
                      Re-scan Theme
                    </s-button>
                  </div>
                )}
                {groups.map(group => (
                  <ThemeGroup
                    key={group.key}
                    group={group}
                    selectedImages={selectedImages}
                    imageStatuses={imageStatuses}
                    isExpanded={expandedGroups.has(group.key)}
                    onToggleExpand={handleToggleExpand}
                    onToggleGroupAll={handleToggleGroupAll}
                    onToggleOne={handleToggleOne}
                    onRevert={() => {}}
                    onCompare={setCompareImg}
                    onOptimizeGroup={(batch) => {
                      const toOptimize = batch.filter(i => selectedImages.has(i.id));
                      openConfirmModal(() => handleOptimizeBatch(toOptimize), toOptimize.length);
                    }}
                    optimizing={optimizing}
                  />
                ))}
              </s-stack>
              </div>
            )}
          </s-stack>
        )}

        {/* ══ TAB 1: Recent Optimizations ══ */}
        {activeTab === 1 && (
          <ThemeRecents refreshKey={recentsKey} onCompare={setCompareImg} />
        )}

      </s-stack>

      <CompareModal data={compareImg} onClose={() => setCompareImg(null)} />

      <OptimizeConfirmModal
        triggerRef={modalTriggerRef}
        isLiveTheme={isLiveTheme}
        imageCount={confirmImageCount}
        onConfirm={handleConfirmOptimize}
      />
    </div>
  );
};

export default ThemeCrushPage;
